import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactPreview } from '../../shared/models';
import { formatPhoneNumber } from '../../shared/phone-format';

@Component({
  standalone: true,
  selector: 'app-chat-header',
  imports: [CommonModule],
  template: `
  <div class="wrap" *ngIf="contact">
    <header class="hdr">
      <button class="back" (click)="back.emit()" aria-label="Back">←</button>
      <button class="identity" type="button" (click)="toggleProfilePanel($event)" aria-label="Open contact options">
        <div class="avatar" aria-hidden="true">
          <svg class="avatarIcon" viewBox="0 0 24 24" fill="none">
            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Z" stroke="currentColor" stroke-width="1.8"/>
            <path d="M5 20a7 7 0 0 1 14 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
          </svg>
        </div>
        <div class="title">
          <div class="name">{{ formatPhone(contact.phone) }}</div>
          <div class="alias">{{ contact.displayName || 'Unknown contact' }}</div>
        </div>
      </button>

      <div class="actions">
        <button class="iconBtn" type="button" aria-label="Search in chat" (click)="toggleSearch($event)">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="6"></circle>
            <path d="M20 20l-4-4"></path>
          </svg>
        </button>
        <button class="iconBtn" type="button" aria-label="More options" (click)="toggleMenu($event)">⋮</button>
      </div>
    </header>

    <div class="sub">
      <span class="pill"
        [class.sales]="contact.mainCategory==='Sales'"
        [class.lead]="contact.mainCategory==='Lead'"
        *ngIf="contact.label">{{ contact.label }}</span>
      <span class="billingWarn" *ngIf="contact.billableOnNextCustomerMessage">
        Reply now is billable
      </span>
      <span class="humanState" [class.on]="contact.handledBy==='human'" [class.off]="contact.handledBy!=='human'">
        {{ contact.handledBy==='human' ? 'Human Handling' : 'Bot Handling' }}
      </span>
    </div>

    <div class="searchRow" *ngIf="searchOpen">
      <div class="searchIn">
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="11" cy="11" r="6"></circle>
          <path d="M20 20l-4-4"></path>
        </svg>
        <input
          type="text"
          [value]="searchQuery"
          (input)="onSearchInput($event)"
          placeholder="Search in this chat"
          aria-label="Search in this chat" />
      </div>
      <button class="clearSearch" type="button" (click)="clearSearch()">Clear</button>
    </div>

    <section class="profilePanel" *ngIf="profilePanelOpen">
      <button type="button" (click)="emitAction('view-contact')">View contact</button>
      <button type="button" (click)="emitAction('media-links-docs')">Media, links, and docs</button>
      <button type="button" class="humanToggle" (click)="toggleHumanRequiredMode()">
        {{ contact?.requiresHumanAgent ? 'Release human takeover' : 'Request human takeover' }}
      </button>
      <button type="button" class="botToggle" (click)="toggleBotMode()">
        {{ contact?.botEnabled ? 'Disable bot (manual reply)' : 'Enable bot (auto reply)' }}
      </button>
    </section>

    <div class="menu" *ngIf="menuOpen" role="menu" aria-label="Chat options">
      <button type="button" *ngFor="let item of menuItems" (click)="emitAction(item.id)">{{ item.label }}</button>
    </div>
  </div>
  `,
  styles: [`
    .wrap{position:relative}
    .hdr{height:64px;display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid var(--border);background:var(--panel)}
    .back{display:none;border:1px solid var(--border);background:transparent;color:var(--text);border-radius:12px;padding:8px 10px;cursor:pointer}
    .identity{
      flex:1;min-width:0;display:flex;align-items:center;gap:12px;padding:0;border:0;background:transparent;color:inherit;cursor:pointer;text-align:left
    }
    .identity:hover .name{text-decoration:underline;text-decoration-color:rgba(0,168,132,.45);text-decoration-thickness:2px}
    .avatar{width:42px;height:42px;border-radius:16px;display:flex;align-items:center;justify-content:center;background:rgba(32,44,51,.55);border:1px solid var(--border);color:rgba(148,163,184,.95)}
    .avatarIcon{width:22px;height:22px;display:block}
    :host-context(body.light) .avatar{background:rgba(0,0,0,.05)}
    .title{min-width:0}
    .name{font-weight:900;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .alias{margin-top:2px;font-size:12px;color:var(--muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
    .sub{
      display:flex;
      gap:10px;
      align-items:center;
      flex-wrap:wrap;
      padding:8px 12px;
      border-bottom:1px solid var(--border);
      background:var(--panel);
    }
    .pill{font-size:11px;padding:3px 8px;border-radius:999px;border:1px solid transparent}
    .pill.sales{border-color:rgba(37,99,235,.35);background:rgba(37,99,235,.14);color:rgb(147,197,253)}
    .pill.lead{border-color:rgba(22,163,74,.35);background:rgba(22,163,74,.14);color:rgb(134,239,172)}
    :host-context(body.light) .pill.sales{
      color:rgb(30,64,175);
      background:rgba(59,130,246,.20);
      border-color:rgba(37,99,235,.45);
    }
    :host-context(body.light) .pill.lead{
      color:rgb(22,101,52);
      background:rgba(34,197,94,.22);
      border-color:rgba(22,163,74,.45);
    }
    .humanState{
      font-size:11px;
      padding:3px 8px;
      border-radius:999px;
      border:1px solid transparent;
      font-weight:700;
      white-space:nowrap;
    }
    .humanState.on{
      color:rgb(253,186,116);
      background:rgba(217,119,6,.18);
      border-color:rgba(217,119,6,.45);
    }
    .humanState.off{
      color:rgb(125,211,252);
      background:rgba(6,182,212,.16);
      border-color:rgba(6,182,212,.4);
    }
    :host-context(body.light) .humanState.on{
      color:rgb(154,52,18);
      background:rgba(251,146,60,.22);
      border-color:rgba(217,119,6,.45);
    }
    :host-context(body.light) .humanState.off{
      color:rgb(14,116,144);
      background:rgba(34,211,238,.22);
      border-color:rgba(6,182,212,.45);
    }
    .billingWarn{
      font-size:11px;
      padding:3px 8px;
      border-radius:999px;
      border:1px solid rgba(225,29,72,.5);
      color:rgb(251,113,133);
      background:rgba(225,29,72,.18);
      font-weight:700;
      white-space:nowrap;
    }
    :host-context(body.light) .billingWarn{
      color:rgb(159,18,57);
      background:rgba(251,113,133,.20);
      border-color:rgba(225,29,72,.40);
    }
    .actions{display:flex;align-items:center;gap:6px}
    .iconBtn{
      width:36px;height:36px;border-radius:10px;border:1px solid transparent;background:transparent;color:var(--muted);cursor:pointer;font-size:16px;
      display:inline-flex;align-items:center;justify-content:center
    }
    .iconBtn svg{width:18px;height:18px;fill:none;stroke:currentColor;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round}
    .iconBtn:hover{color:var(--text);background:rgba(42,57,66,.45);border-color:rgba(42,57,66,.65)}
    .searchRow{
      display:flex;
      align-items:center;
      gap:8px;
      padding:8px 12px;
      border-bottom:1px solid var(--border);
      background:var(--panel);
    }
    .searchIn{
      flex:1;
      min-width:0;
      display:flex;
      align-items:center;
      gap:8px;
      border:1px solid var(--border);
      background:var(--input);
      border-radius:10px;
      padding:0 10px;
      height:34px;
    }
    .searchIn svg{
      width:15px;
      height:15px;
      fill:none;
      stroke:var(--muted);
      stroke-width:2;
      stroke-linecap:round;
      stroke-linejoin:round;
      flex:0 0 auto;
    }
    .searchIn input{
      width:100%;
      border:0;
      outline:0;
      background:transparent;
      color:var(--text);
      font-size:13px;
    }
    .clearSearch{
      border:1px solid var(--border);
      background:transparent;
      color:var(--text);
      border-radius:10px;
      height:34px;
      padding:0 10px;
      cursor:pointer;
      font-size:12px;
    }
    .clearSearch:hover{border-color:rgba(0,168,132,.35);background:rgba(0,168,132,.10)}
    .profilePanel{
      display:flex;gap:8px;flex-wrap:wrap;padding:10px 12px;border-bottom:1px solid var(--border);background:var(--panel)
    }
    .profilePanel button{
      border:1px solid var(--border);background:transparent;color:var(--text);border-radius:999px;padding:5px 10px;font-size:12px;cursor:pointer
    }
    .profilePanel button:hover{border-color:rgba(0,168,132,.35);background:rgba(0,168,132,.10)}
    .profilePanel .humanToggle{border-color:rgba(217,119,6,.45);background:rgba(217,119,6,.12)}
    .profilePanel .humanToggle:hover{border-color:rgba(217,119,6,.62);background:rgba(217,119,6,.2)}
    .profilePanel .botToggle{border-color:rgba(6,182,212,.45);background:rgba(6,182,212,.10)}
    .profilePanel .botToggle:hover{border-color:rgba(6,182,212,.60);background:rgba(6,182,212,.18)}
    .menu{
      position:absolute;right:12px;top:56px;z-index:20;min-width:220px;
      border:1px solid var(--border);border-radius:12px;padding:6px;background:var(--panel);box-shadow:0 12px 30px rgba(0,0,0,.28)
    }
    .menu button{
      width:100%;text-align:left;border:0;background:transparent;color:var(--text);padding:9px 10px;border-radius:8px;cursor:pointer;font-size:13px
    }
    .menu button:hover{background:rgba(42,57,66,.45)}
    @media (max-width: 900px){ .back{display:inline-flex} }
  `]
})
export class ChatHeaderComponent {
  @Input() contact: ContactPreview | null = null;
  @Output() back = new EventEmitter<void>();
  @Output() action = new EventEmitter<string>();
  @Output() toggleBot = new EventEmitter<boolean>();
  @Output() toggleHumanRequired = new EventEmitter<boolean>();
  @Output() searchQueryChange = new EventEmitter<string>();

  menuOpen = false;
  profilePanelOpen = false;
  searchOpen = false;
  searchQuery = '';

  menuItems = [
    { id: 'enable-bot', label: 'Enable bot' },
  ];

  constructor(private host: ElementRef<HTMLElement>) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(e: MouseEvent): void {
    const target = e.target as Node | null;
    if (!target) return;
    if (!this.host.nativeElement.contains(target)) {
      this.menuOpen = false;
      this.profilePanelOpen = false;
    }
  }

  toggleMenu(e: MouseEvent): void {
    e.stopPropagation();
    this.menuOpen = !this.menuOpen;
    this.profilePanelOpen = false;
  }

  toggleProfilePanel(e: MouseEvent): void {
    e.stopPropagation();
    this.profilePanelOpen = !this.profilePanelOpen;
    this.menuOpen = false;
  }

  toggleSearch(e: MouseEvent): void {
    e.stopPropagation();
    this.searchOpen = !this.searchOpen;
    this.menuOpen = false;
    this.profilePanelOpen = false;
    if (!this.searchOpen) this.clearSearch();
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement | null;
    this.searchQuery = target?.value || '';
    this.searchQueryChange.emit(this.searchQuery);
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.searchQueryChange.emit('');
    this.searchOpen = false;
  }

  emitAction(actionId: string): void {
    this.action.emit(actionId);
    this.menuOpen = false;
  }

  toggleBotMode(): void {
    if (!this.contact) return;
    this.toggleBot.emit(!this.contact.botEnabled);
    this.profilePanelOpen = false;
  }

  toggleHumanRequiredMode(): void {
    if (!this.contact) return;
    this.toggleHumanRequired.emit(!this.contact.requiresHumanAgent);
    this.profilePanelOpen = false;
  }

  formatPhone(value: string): string {
    return formatPhoneNumber(value);
  }
}
